import PersonIcon from '@mui/icons-material/Person'
import { Button } from '@mui/material'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import { useGetSiteAdminUserDetail } from '@/features/dashboard/site-admin/users/api/get-user-detail'
import { SuspensionStatusChip } from '@/features/dashboard/site-admin/users/components/SuspensionStatusChip'
import EntityLink from '../EntityLink'
import PeekLayout, {
    PeekDetails,
    PeekPlaceholder,
    PeekSection
} from './PeekLayout'

// The detail endpoint returns at most this many warnings (see
// SiteAdminUserDetailSerializer.get_warnings), so a full list may be more.
const WARNINGS_PAGE = 25

/**
 * A platform user, for site admins: who they are, which organisation they
 * belong to, and whether they're in good standing. Uses the same query as the
 * site-admin user page, so opening one after the other is instant.
 */
export default function UserPeek({ id }) {
    const query = useGetSiteAdminUserDetail(id, { retry: false })
    const user = query.data

    if (!user) {
        return <PeekPlaceholder kind="User" loading={query.isLoading} />
    }

    const warnings = user.warnings?.length ?? 0
    const suspensionReason =
        user.active_suspension?.reason || user.suspension_reason

    return (
        <PeekLayout
            kind="User"
            title={user.name || user.username}
            subtitle={`@${user.username}`}
            actions={
                <Button
                    component={RouterLink}
                    to={`/site-admin/users/${user.id}`}
                    size="small"
                    variant="outlined"
                    startIcon={<PersonIcon fontSize="small" />}
                >
                    Open user
                </Button>
            }
        >
            <PeekSection title="Details">
                <PeekDetails
                    rows={[
                        { label: 'Email', value: user.email || '—' },
                        {
                            label: 'Organisation',
                            value: user.org ? (
                                <EntityLink type="organisation" id={user.org}>
                                    {user.org_name}
                                </EntityLink>
                            ) : (
                                'None'
                            )
                        },
                        {
                            label: 'Joined',
                            value: user.date_joined
                                ? DateTime.fromISO(
                                      user.date_joined
                                  ).toLocaleString(DateTime.DATE_MED)
                                : '—'
                        }
                    ]}
                />
            </PeekSection>

            <PeekSection title="Standing">
                <PeekDetails
                    rows={[
                        {
                            label: 'Status',
                            value: (
                                <SuspensionStatusChip
                                    isSuspended={Boolean(user.is_suspended)}
                                    liftedAt={user.suspension_lifted_at}
                                />
                            )
                        },
                        user.is_suspended && suspensionReason
                            ? { label: 'Reason', value: suspensionReason }
                            : null,
                        {
                            label: 'Warnings',
                            value:
                                warnings >= WARNINGS_PAGE
                                    ? `${WARNINGS_PAGE}+`
                                    : warnings
                        },
                        {
                            label: 'Reports against',
                            value: user.reports_against_count ?? 0
                        }
                    ]}
                />
            </PeekSection>
        </PeekLayout>
    )
}

UserPeek.propTypes = {
    id: PropTypes.string.isRequired
}
